import { Fragment, useState } from "react";
import { formatAmount } from "@/utils/formatAmount";
import { Link, useParams } from "react-router-dom";
import {
  Award,
  ArrowLeft,
  Ban,
  Eye,
  EyeOff,
  FileText,
  Globe,
  Lock,
  Paperclip,
  Pencil,
  Send,
  Trophy,
  Users,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { useAuthStore } from "@/store/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  CANCEL_REASONS,
  downloadProtocol,
  useProceedingPanel,
  usePublishProceeding,
  useResolutionPreview,
  useResolveProceeding,
} from "@/features/rfq/api/proceedings";
import { ProceedingOffererViewModal } from "@/features/rfq/components/ProceedingOffererViewModal";
import { PublishDialog } from "@/features/rfq/components/PublishDialog";
import { ProceedingFormModal } from "@/features/rfq/components/ProceedingFormModal";
import { GenerateOrderModal } from "@/features/rfq/components/GenerateOrderModal";
import { Countdown } from "@/features/rfq/components/Countdown";
import { RunoffPanel } from "@/features/rfq/components/RunoffPanel";
import { CommunicationsPanel } from "@/features/rfq/components/CommunicationsPanel";
import { QuestionsGk } from "@/features/rfq/components/QuestionsGk";

/**
 * Panel jednego postępowania — wszystko, czego kupiec potrzebuje w jednym miejscu.
 *
 * Sedno to trzy rzeczy, których nie widać na liście: co dokładnie widzi oferent,
 * kto ogłoszenie otworzył i kto jeszcze nie zajrzał. Cisza w postępowaniu ma
 * zupełnie inny ciężar, gdy nikt nie zaglądał, niż gdy zajrzeli wszyscy i odpuścili.
 */

export function ProceedingPanelPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { data, isLoading } = useProceedingPanel(id);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editing, setEditing] = useState(false);
  const publish = usePublishProceeding();
  const resolve = useResolveProceeding(id ?? "");
  const [resolving, setResolving] = useState<"award" | "cancel" | null>(null);
  const [pickedOffer, setPickedOffer] = useState("");
  const [cancelReason, setCancelReason] = useState("no_offers");
  const [note, setNote] = useState("");
  // Drugi krok rozstrzygnięcia: zanim maile wyjdą, kupiec MUSI zobaczyć osobny ekran
  // podglądu (do kogo, jaka treść) i kliknąć wysyłkę jeszcze raz — nie da się przeoczyć.
  const [confirming, setConfirming] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [expandedOffer, setExpandedOffer] = useState<string | null>(null);

  const closeResolve = () => {
    setResolving(null);
    setConfirming(false);
  };
  const { data: mailPreview } = useResolutionPreview(
    id ?? "",
    resolving ?? "award",
    resolving === "award" ? pickedOffer || null : null,
    !!resolving,
  );
  usePageTitle(data ? `${data.proceeding.number}` : "Postępowanie");

  if (isLoading || !data) return <PageLoader />;
  const {
    proceeding,
    offerer_preview: preview,
    audience,
    offers,
    stats,
  } = data;
  const mayManage = !!user?.is_ceo || !!user?.has_rfq_manage_permission;

  return (
    <div className="max-w-5xl">
      <Link
        to="/rfq/proceedings"
        className="mb-5 inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      >
        <ArrowLeft size={14} /> Postępowania
      </Link>

      {/* Nagłówek */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-[var(--color-text-muted)]">
            {proceeding.number}
          </span>
          <Badge color={proceeding.status === "published" ? "green" : "gray"}>
            {proceeding.status_label}
          </Badge>
          <Badge color="blue">{proceeding.access_mode_label}</Badge>
          {proceeding.status === "published" &&
            !proceeding.is_open_for_offers && (
              <Badge color="yellow">Po terminie</Badge>
            )}
          {proceeding.is_runoff_open && <Badge color="orange">Dogrywka trwa</Badge>}
          {/* Tura się skończyła, a wynik nie zapadł — to samo, co liczy badge w menu. */}
          {proceeding.awaits_resolution && (
            <Badge color="red">Czeka na rozstrzygnięcie</Badge>
          )}
          {proceeding.is_public && <Badge color="blue">Publiczne</Badge>}
        </div>
        <h1 className="mt-1 text-2xl font-bold leading-snug text-[var(--color-text-primary)]">
          {proceeding.title}
        </h1>
        <div className="mt-2 h-0.5 w-8 bg-[var(--color-primary)]" />
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-[var(--color-text-secondary)]">
          <span>
            Termin składania:{" "}
            <strong className="text-[var(--color-text-primary)]">
              {new Date(proceeding.submission_deadline).toLocaleString("pl")}
            </strong>
          </span>
          {proceeding.delivery_deadline && (
            <span>
              Realizacja do:{" "}
              <strong className="text-[var(--color-text-primary)]">
                {new Date(proceeding.delivery_deadline).toLocaleDateString(
                  "pl",
                )}
              </strong>
            </span>
          )}
          {proceeding.status === "published" && (
            <Countdown deadline={proceeding.submission_deadline} />
          )}
        </div>
      </div>

      {/* Liczby */}
      <div
        className={`mb-6 grid gap-px overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-border)] ${
          stats.guest_count === null
            ? "sm:grid-cols-3"
            : "sm:grid-cols-2 lg:grid-cols-4"
        }`}
      >
        <Stat
          label="Objętych ogłoszeniem"
          value={stats.eligible_count}
          Icon={Users}
        />
        <Stat label="Otworzyło" value={stats.seen_count} Icon={Eye} />
        {/* Kafelek tylko przy ogłoszeniu publicznym — przy zamkniętym „0 gości"
            sugerowałoby, że ktoś z ulicy mógł tu zajrzeć i nie chciał. */}
        {stats.guest_count !== null && (
          <Stat
            label="Gości bez konta"
            value={stats.guest_count}
            Icon={Globe}
            hint={
              stats.guest_open_count
                ? `${stats.guest_open_count} ${plWejsc(stats.guest_open_count)}`
                : undefined
            }
          />
        )}
        <Stat label="Złożonych ofert" value={stats.offers_count} Icon={Send} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Nasze dane */}
        <section>
          <SectionTitle>Dane wewnętrzne</SectionTitle>
          <dl className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-2 text-sm">
            <Row label="Kupujący" value={proceeding.responsible_name ?? "—"} />
            <Row
              label="Przedsięwzięcie"
              value={proceeding.project_name ?? "— bez powiązania —"}
            />
            <Row
              label="Cena maksymalna"
              value={
                proceeding.max_price_net
                  ? `${proceeding.max_price_net} zł netto ${
                      proceeding.max_price_visible
                        ? "(jawna)"
                        : "(ukryta przed oferentami)"
                    }`
                  : "— nie ustalono —"
              }
            />
            <Row
              label="Czego dotyczy cena"
              value={proceeding.price_basis || "— całość zamówienia —"}
            />
            <Row
              label="Województwo"
              value={
                proceeding.voivodeship_label
                  ? proceeding.county_labels.length
                    ? `${proceeding.voivodeship_label} — powiaty: ${proceeding.county_labels.join(", ")}`
                    : proceeding.voivodeship_label
                  : "— nie jest kryterium —"
              }
            />
            {proceeding.access_mode === "invited" && (
              <Row
                label="Zaproszone firmy"
                value={
                  proceeding.invited_detail.map((c) => c.name).join(", ") || "—"
                }
              />
            )}
            <Row
              label="Zakres prac"
              value={
                proceeding.positions_detail
                  .map((p) => `${p.category_name}: ${p.name}`)
                  .join(" · ") || "—"
              }
            />
            {proceeding.excluded_detail.length > 0 && (
              <Row
                label="Wykluczone firmy"
                value={
                  proceeding.excluded_detail.map((c) => c.name).join(", ") +
                  " — wykluczenie dotyczy tylko tego postępowania"
                }
              />
            )}
          </dl>

          {/* Protokół: dokument wewnętrzny z cenami maksymalnymi i notatkami, więc
              bramka jest na dyrektorze technicznym — jak po stronie API. */}
          {(user?.is_ceo || user?.is_technical_director) && (
            <div className="mt-3">
              <Button
                variant="secondary"
                onClick={() =>
                  downloadProtocol(proceeding.id, proceeding.number).catch(() =>
                    alert("Nie udało się pobrać protokołu."),
                  )
                }
              >
                <FileText size={14} className="mr-1.5 inline" /> Protokół (PDF)
              </Button>
            </div>
          )}

          {mayManage && proceeding.status === "draft" && (
            <div className="mt-3 flex flex-wrap gap-2">
              {/* Szkic wolno zmieniać do woli — ten sam formularz co na liście
                  (kupujący, tryb dostępu, cena, terminy, załączniki). */}
              <Button variant="secondary" onClick={() => setEditing(true)}>
                <Pencil size={14} className="mr-1.5 inline" /> Edytuj szkic
              </Button>
              {/* Publikacja przez osobny ekran: zamrożenie treści i wybór adresatów
                  zaproszenia mailowego to dwie nieodwracalne decyzje naraz. */}
              <Button
                onClick={() => setPublishing(true)}
                disabled={publish.isPending}
              >
                <Send size={14} className="mr-1.5 inline" /> Opublikuj
              </Button>
            </div>
          )}
        </section>

        {/* Treść ogłoszenia — opis i załączniki, czyli to, po co oferent tu wchodzi */}
        <section>
          <SectionTitle>
            <FileText size={14} className="mr-1 inline" /> Treść ogłoszenia
          </SectionTitle>
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4 text-sm">
            {proceeding.description ? (
              <p className="whitespace-pre-wrap text-[var(--color-text-secondary)]">
                {proceeding.description}
              </p>
            ) : (
              <p className="text-[var(--color-text-muted)]">Brak opisu.</p>
            )}

            <p className="mt-4 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
              Załączniki ({proceeding.attachments.length})
            </p>
            {proceeding.attachments.length === 0 ? (
              <p className="text-[var(--color-text-muted)]">
                Brak załączników.
              </p>
            ) : (
              <ul className="mt-1 space-y-1">
                {proceeding.attachments.map((attachment) => (
                  <li
                    key={attachment.id}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <ManagedFileLink
                      file={attachment}
                      icon={<Paperclip size={12} />}
                    >
                      {attachment.label || attachment.original_filename}
                    </ManagedFileLink>
                    {attachment.label && (
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {attachment.original_filename}
                      </span>
                    )}
                    <span className="text-xs text-[var(--color-text-muted)]">
                      · dodano{" "}
                      {new Date(attachment.created_at).toLocaleDateString("pl")}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 border-t border-[var(--color-border)] pt-3">
              <Button variant="secondary" onClick={() => setPreviewOpen(true)}>
                <Eye size={14} className="mr-1.5 inline" /> Zobacz jak oferent
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Kto widział */}
      <section className="mt-6">
        <SectionTitle>Krąg odbiorców ({audience.length})</SectionTitle>
        <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-xs">
            <thead className="bg-[var(--color-bg-elevated)] text-left">
              <tr>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">
                  Firma
                </th>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">
                  Otworzyła
                </th>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">
                  Pierwsze wejście
                </th>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">
                  Ostatnie
                </th>
                <th className="px-4 py-2 text-right font-mono text-[var(--color-text-muted)]">
                  Wejść
                </th>
              </tr>
            </thead>
            <tbody>
              {audience.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-elevated)]"
                >
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/contractors/${row.id}`}
                      className="text-[var(--color-primary)] hover:underline"
                    >
                      {row.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5">
                    {row.seen ? (
                      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                        <Eye size={12} /> tak
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[var(--color-text-muted)]">
                        <EyeOff size={12} /> nie
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-[var(--color-text-secondary)]">
                    {row.first_seen_at
                      ? new Date(row.first_seen_at).toLocaleString("pl")
                      : "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-[var(--color-text-secondary)]">
                    {row.last_seen_at
                      ? new Date(row.last_seen_at).toLocaleString("pl")
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-[var(--color-text-secondary)]">
                    {row.open_count || "—"}
                  </td>
                </tr>
              ))}
              {audience.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-6 text-center text-[var(--color-text-muted)]"
                  >
                    Ogłoszenie nikogo nie obejmuje — sprawdź zakres prac i tryb
                    dostępu.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Komunikaty i zmiany */}
      <section className="mt-6">
        <SectionTitle>Komunikaty i zmiany</SectionTitle>
        <CommunicationsPanel
          proceedingId={proceeding.id}
          canPost={mayManage && proceeding.status === "published"}
        />
      </section>

      {/* Pytania oferentów (tajne) */}
      <section className="mt-6">
        <SectionTitle>Pytania oferentów</SectionTitle>
        <QuestionsGk proceedingId={proceeding.id} canAnswer={mayManage} />
      </section>

      {/* Dogrywka — druga tura po otwarciu ofert */}
      {(proceeding.status === "published" || proceeding.has_runoff) && (
        <section className="mt-6">
          <SectionTitle>Dogrywka</SectionTitle>
          <RunoffPanel
            proceeding={proceeding}
            offers={offers}
            canManage={mayManage}
          />
        </section>
      )}

      {/* Rozstrzygnięcie */}
      {proceeding.status === "awarded" || proceeding.status === "cancelled" ? (
        <section className="mt-6">
          <SectionTitle>Rozstrzygnięcie</SectionTitle>
          <div
            className={`rounded-lg border border-l-4 border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4 ${
              proceeding.status === "awarded"
                ? "border-l-green-500"
                : "border-l-[var(--color-text-muted)]"
            }`}
          >
            {proceeding.status === "awarded" ? (
              <p className="flex items-center gap-2 text-sm">
                <Trophy
                  size={16}
                  className="text-green-600 dark:text-green-400"
                />
                <span className="text-[var(--color-text-primary)]">
                  Wybrano: <strong>{proceeding.winning_contractor_name}</strong>
                </span>
              </p>
            ) : (
              <p className="flex items-center gap-2 text-sm">
                <Ban size={16} className="text-[var(--color-text-muted)]" />
                <span className="text-[var(--color-text-primary)]">
                  Anulowano — {proceeding.cancel_reason_label}
                </span>
              </p>
            )}
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              {proceeding.resolved_by_name} ·{" "}
              {proceeding.resolved_at &&
                new Date(proceeding.resolved_at).toLocaleString("pl")}
            </p>
            {proceeding.resolution_note && (
              <p className="mt-2 text-xs text-[var(--color-text-secondary)]">
                Notatka wewnętrzna: {proceeding.resolution_note}
              </p>
            )}
          </div>

          {/* Domknięcie pętli: ze zwycięskiej oferty robimy Zamówienie do wykonawcy. */}
          {proceeding.status === "awarded" &&
            (proceeding.generated_order ? (
              <Link
                to={`/orders/${proceeding.generated_order}`}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:underline"
              >
                <FileText size={14} /> Zamówienie:{" "}
                {proceeding.generated_order_number}
              </Link>
            ) : proceeding.stage_link ? (
              /* Postępowanie prowadzi etap projektowy (DLC_01). Zamówienie tworzy się TAM —
                 tylko ta droga przechodzi przez zapotrzebowanie ze znacznikiem etapu, więc
                 tylko po niej etap widzi wykonawcę, punkty MDCP się przypinają, a geodeta
                 dostaje obrys. Backend blokuje tu generowanie; przycisku nie pokazujemy. */
              <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
                <p className="text-sm text-[var(--color-text-primary)]">
                  Postępowanie prowadzi etap <strong>{proceeding.stage_link.path_label}</strong>{" "}
                  przedsięwzięcia — zamówienie tworzy kierownik przyciskiem „Zleć zwycięzcy" w
                  panelu etapu.
                </p>
                <Link
                  to={`/power-projects/${proceeding.stage_link.project_id}`}
                  className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:underline"
                >
                  <FileText size={14} /> Przejdź do przedsięwzięcia
                </Link>
              </div>
            ) : (
              mayManage &&
              user?.has_orderer_permission && (
                <div className="mt-3">
                  <Button onClick={() => setShowGenerate(true)}>
                    <FileText size={14} className="mr-1.5 inline" /> Generuj
                    zamówienie
                  </Button>
                </div>
              )
            ))}
        </section>
      ) : (
        mayManage &&
        proceeding.status === "published" && (
          <section className="mt-6">
            <SectionTitle>Rozstrzygnięcie</SectionTitle>
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
              {/* Trwająca dogrywka blokuje rozstrzygnięcie tak samo jak nieminięty
                  termin — w obu przypadkach zamknięcie ucina turę, którą sami daliśmy
                  oferentom, więc wolno je tylko z „Oferty przed otwarciem”. */}
              {(proceeding.is_open_for_offers || proceeding.is_runoff_open) &&
              !data.can_see_sealed_amounts ? (
                <p className="text-sm text-[var(--color-text-secondary)]">
                  {proceeding.is_runoff_open
                    ? "Trwa dogrywka — oferenci mogą jeszcze obniżyć kwoty. Rozstrzygnąć będzie można po jej zakończeniu; wcześniejsze zamknięcie wymaga uprawnienia „Oferty przed otwarciem”."
                    : "Postępowanie wciąż trwa. Rozstrzygnąć będzie można po terminie składania ofert. Wcześniejsze zamknięcie (wybór lub anulowanie w trakcie) wymaga uprawnienia „Oferty przed otwarciem”."}
                </p>
              ) : !resolving ? (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="flex-1 text-sm text-[var(--color-text-secondary)]">
                    Wybierz zwycięską ofertę albo zamknij postępowanie bez
                    wyboru. Do każdego oferenta pójdzie <strong>e-mail</strong>{" "}
                    z odpowiednią klauzulą.
                    {proceeding.is_open_for_offers && (
                      <span className="mt-1 block text-xs text-amber-600 dark:text-amber-500">
                        Uwaga: postępowanie jeszcze trwa — zamkniesz je przed
                        terminem.
                      </span>
                    )}
                    {proceeding.is_runoff_open && (
                      <span className="mt-1 block text-xs text-amber-600 dark:text-amber-500">
                        Uwaga: trwa dogrywka — rozstrzygnięcie teraz ucina ją
                        przed czasem, a oferenci mogą jeszcze schodzić z ceny.
                      </span>
                    )}
                  </p>
                  <Button onClick={() => setResolving("award")}>
                    <Award size={14} className="mr-1.5 inline" /> Wybierz ofertę
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setResolving("cancel")}
                  >
                    <Ban size={14} className="mr-1.5 inline" /> Anuluj
                  </Button>
                </div>
              ) : confirming ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    {resolving === "award"
                      ? "Ostatni krok — sprawdź, jakie zawiadomienia pójdą i do kogo:"
                      : "Ostatni krok — sprawdź, jakie zawiadomienia pójdą i do kogo:"}
                  </p>
                  <MailPreview rows={mailPreview} />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => setConfirming(false)}
                    >
                      <X size={14} className="mr-1 inline" /> Wróć do edycji
                    </Button>
                    <Button
                      onClick={() => {
                        if (resolving === "award") {
                          if (!pickedOffer) return;
                          resolve.mutate(
                            { action: "award", offer: pickedOffer, note },
                            {
                              onSuccess: (m) =>
                                alert(
                                  `Rozstrzygnięto. Zawiadomiono ${m.notified} firm(y).`,
                                ),
                            },
                          );
                        } else {
                          resolve.mutate(
                            {
                              action: "cancel",
                              cancel_reason: cancelReason,
                              note,
                            },
                            {
                              onSuccess: (m) =>
                                alert(
                                  `Anulowano. Zawiadomiono ${m.notified} firm(y).`,
                                ),
                            },
                          );
                        }
                      }}
                      disabled={
                        resolve.isPending ||
                        (resolving === "award" && !pickedOffer)
                      }
                    >
                      <Send size={14} className="mr-1.5 inline" />
                      {resolving === "award"
                        ? "Wyślij zawiadomienia i rozstrzygnij"
                        : "Wyślij zawiadomienia i anuluj"}
                    </Button>
                  </div>
                </div>
              ) : resolving === "award" ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    Wskaż zwycięską ofertę:
                  </p>
                  {offers.filter((o) => o.status === "submitted").length ===
                  0 ? (
                    <p className="text-sm text-[var(--color-text-muted)]">
                      Brak aktywnych ofert — można tylko anulować.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {offers
                        .filter((o) => o.status === "submitted")
                        .map((o) => (
                          <label
                            key={o.id}
                            className="flex items-center gap-2 text-sm"
                          >
                            <input
                              type="radio"
                              name="winner"
                              checked={pickedOffer === o.id}
                              onChange={() => setPickedOffer(o.id)}
                            />
                            <span className="text-[var(--color-text-primary)]">
                              {o.contractor_name}
                              {o.amount_net !== null &&
                                ` — ${formatAmount(o.amount_net)} zł`}
                            </span>
                          </label>
                        ))}
                    </div>
                  )}
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    placeholder="Notatka wewnętrzna (opcjonalnie) — nie trafia do oferentów"
                    className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={closeResolve}>
                      <X size={14} className="mr-1 inline" /> Wróć
                    </Button>
                    <Button
                      onClick={() => setConfirming(true)}
                      disabled={!pickedOffer}
                    >
                      Dalej — podgląd maili
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    Powód anulowania:
                  </p>
                  <select
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
                  >
                    {CANCEL_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    placeholder="Notatka wewnętrzna (opcjonalnie) — nie trafia do oferentów"
                    className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={closeResolve}>
                      <X size={14} className="mr-1 inline" /> Wróć
                    </Button>
                    <Button onClick={() => setConfirming(true)}>
                      Dalej — podgląd maili
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )
      )}

      {/* Oferty */}
      <section className="mt-6">
        <SectionTitle>Oferty ({stats.offers_count})</SectionTitle>
        {!data.amounts_visible && (
          <p className="mb-2 flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
            <Lock size={12} /> Koperta zamknięta — kwoty odsłonią się po
            terminie składania.
            {data.can_see_sealed_amounts
              ? " Masz uprawnienie „Oferty przed otwarciem”, więc widzisz je już teraz."
              : " Bez uprawnienia „Oferty przed otwarciem” nie zobaczysz ich wcześniej."}
          </p>
        )}
        <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-xs">
            <thead className="bg-[var(--color-bg-elevated)] text-left">
              <tr>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">
                  #
                </th>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">
                  Firma
                </th>
                {proceeding.has_runoff && (
                  <th className="px-4 py-2 text-right font-mono text-[var(--color-text-muted)]">
                    I tura
                  </th>
                )}
                <th className="px-4 py-2 text-right font-mono text-[var(--color-text-muted)]">
                  {proceeding.has_runoff ? "Po dogrywce" : "Kwota netto"}
                </th>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">
                  Status
                </th>
                <th className="px-4 py-2 font-mono text-[var(--color-text-muted)]">
                  Złożono
                </th>
              </tr>
            </thead>
            <tbody>
              {offers.map((offer) => {
                const answers = Object.entries(offer.declaration_answers ?? {});
                const isOpen = expandedOffer === offer.id;
                return (
                  <Fragment key={offer.id}>
                    <tr
                      className={`cursor-pointer border-t border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-elevated)] ${
                        offer.status === "withdrawn" ? "opacity-50" : ""
                      }`}
                      onClick={() => setExpandedOffer(isOpen ? null : offer.id)}
                    >
                      <td className="px-4 py-2.5 tabular-nums text-[var(--color-text-muted)]">
                        {offer.rank ?? "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        <Link
                          to={`/contractors/${offer.contractor}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[var(--color-primary)] hover:underline"
                        >
                          {offer.contractor_name}
                        </Link>
                        {/* Spinacz w wierszu, bo pliki leżą w rozwinięciu — bez niego
                            nikt by nie zgadł, że jest co otwierać. */}
                        {offer.attachment_count > 0 && (
                          <span
                            className="ml-1.5 inline-flex items-center gap-0.5 text-[var(--color-text-muted)]"
                            title={`Załączniki oferty: ${offer.attachment_count}`}
                          >
                            <Paperclip size={11} />
                            {offer.attachment_count}
                          </span>
                        )}
                      </td>
                      {/* Kwota sprzed dogrywki obok aktualnej — widać, kto zszedł. */}
                      {proceeding.has_runoff && (
                        <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-[var(--color-text-muted)]">
                          {offer.first_round_amount_net === null
                            ? "—"
                            : `${formatAmount(offer.first_round_amount_net)} zł`}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-4 py-2.5 text-right font-medium tabular-nums text-[var(--color-text-primary)]">
                        {offer.amount_net === null ? (
                          <span className="inline-flex items-center gap-1 text-[var(--color-text-muted)]">
                            <Lock size={11} /> ukryte
                          </span>
                        ) : (
                          <>
                            {formatAmount(offer.amount_net)} zł
                            {offer.exceeds_max_price && (
                              <span
                                className="ml-1 text-red-600 dark:text-red-400"
                                title="Powyżej ceny maksymalnej"
                              >
                                ⚠
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge
                          color={offer.status === "submitted" ? "green" : "gray"}
                        >
                          {offer.status_label}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-[var(--color-text-secondary)]">
                        {new Date(offer.submitted_at).toLocaleString("pl")}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                        <td
                          colSpan={proceeding.has_runoff ? 6 : 5}
                          className="px-4 py-3"
                        >
                          {offer.runoff_note && (
                            <div className="mb-3 border-l-2 border-[var(--color-primary)] pl-2">
                              <p className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                                Dopytanie indywidualne wysłane przy dogrywce
                              </p>
                              <p className="whitespace-pre-wrap text-sm text-[var(--color-text-secondary)]">
                                {offer.runoff_note}
                              </p>
                            </div>
                          )}
                          {offer.attachment_count > 0 && (
                            <div className="mb-3">
                              <p className="mb-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                                Załączniki oferty ({offer.attachment_count})
                              </p>
                              {offer.attachments.length === 0 ? (
                                // Koperta zamknięta: kosztorys zdradziłby kwotę, więc
                                // do terminu widać tylko, że coś jest.
                                <p className="flex items-center gap-1.5 text-sm text-[var(--color-text-muted)]">
                                  <Lock size={12} /> Odsłonią się razem z kwotami.
                                </p>
                              ) : (
                                <ul className="space-y-1">
                                  {offer.attachments.map((attachment) => (
                                    <li
                                      key={attachment.id}
                                      className="flex flex-wrap items-center gap-2"
                                    >
                                      <ManagedFileLink
                                        file={attachment}
                                        icon={<Paperclip size={12} />}
                                      >
                                        {attachment.original_filename}
                                      </ManagedFileLink>
                                      <span className="text-xs text-[var(--color-text-muted)]">
                                        · dodano{" "}
                                        {new Date(
                                          attachment.created_at,
                                        ).toLocaleDateString("pl")}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                          <p className="mb-2 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                            Oświadczenia oferenta
                          </p>
                          {answers.length === 0 ? (
                            <p className="text-sm text-[var(--color-text-muted)]">
                              Brak oświadczeń w tym postępowaniu.
                            </p>
                          ) : (
                            <ul className="space-y-1">
                              {answers.map(([q, val]) => (
                                <li
                                  key={q}
                                  className="flex items-center justify-between gap-3 text-sm"
                                >
                                  <span className="text-[var(--color-text-primary)]">
                                    {q}
                                  </span>
                                  <Badge color={val ? "green" : "gray"}>
                                    {val ? "TAK" : "NIE"}
                                  </Badge>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {offers.length === 0 && (
                <tr>
                  <td
                    colSpan={proceeding.has_runoff ? 6 : 5}
                    className="px-4 py-6 text-center text-[var(--color-text-muted)]"
                  >
                    Brak ofert.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      <ProceedingOffererViewModal
        proceeding={preview}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
      {editing && (
        <ProceedingFormModal
          initial={proceeding}
          onClose={() => setEditing(false)}
        />
      )}
      {showGenerate && (
        <GenerateOrderModal
          proceeding={proceeding}
          open={showGenerate}
          onClose={() => setShowGenerate(false)}
        />
      )}
      {publishing && (
        <PublishDialog
          proceedingId={proceeding.id}
          onClose={() => setPublishing(false)}
        />
      )}
    </div>
  );
}

function MailPreview({
  rows,
}: {
  rows: import("@/features/rfq/api/proceedings").ResolutionMailPreview[] | undefined;
}) {
  if (!rows) return null;
  if (rows.length === 0)
    return (
      <p className="text-xs text-[var(--color-text-muted)]">
        Brak aktywnych ofert — nikt nie dostanie zawiadomienia.
      </p>
    );
  return (
    <div className="space-y-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
      <p className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
        Zawiadomienia, które pójdą ({rows.length}) — każda firma osobnym mailem
      </p>
      {rows.map((r) => (
        <div key={r.contractor_id} className="border-l-2 border-[var(--color-border)] pl-2 text-xs">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-[var(--color-text-primary)]">
              {r.contractor_name}
            </span>
            <span className="text-[var(--color-text-muted)]">{r.kind_label}</span>
            {r.mail_enabled ? (
              <span className="text-[var(--color-text-muted)]">
                {r.emails.length ? r.emails.join(", ") : "brak adresu"}
              </span>
            ) : (
              <span className="text-amber-600 dark:text-amber-500">mail wyłączony</span>
            )}
          </div>
          <p className="mt-0.5 text-[var(--color-text-secondary)]">
            <span className="text-[var(--color-text-muted)]">Temat:</span> {r.subject}
          </p>
          <p className="whitespace-pre-wrap text-[var(--color-text-secondary)]">{r.body}</p>
        </div>
      ))}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2.5 border-l-2 border-[var(--color-primary)] pl-2.5 text-sm font-semibold leading-5 text-[var(--color-text-primary)]">
      {children}
    </h2>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-4 border-b border-[var(--color-border)] py-2.5 last:border-0">
      <dt className="w-40 shrink-0 pt-px font-mono text-[11px] uppercase leading-5 tracking-wide text-[var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="min-w-0 break-words leading-5 text-[var(--color-text-primary)]">
        {value}
      </dd>
    </div>
  );
}

function Stat({
  label,
  value,
  Icon,
  hint,
}: {
  label: string;
  value: number;
  Icon: typeof Users;
  hint?: string;
}) {
  return (
    <div className="bg-[var(--color-bg-surface)] p-4">
      <p className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
        <Icon size={12} /> {label}
      </p>
      <p className="mt-1 text-3xl font-semibold tabular-nums leading-none text-[var(--color-text-primary)]">
        {value}
      </p>
      {hint && (
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">{hint}</p>
      )}
    </div>
  );
}

function plWejsc(n: number) {
  const last = n % 10;
  const teens = n % 100;
  if (n === 1) return "wejście";
  if (last >= 2 && last <= 4 && (teens < 12 || teens > 14)) return "wejścia";
  return "wejść";
}
